import React, { useEffect, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert'; // Import
import DataTable from 'react-data-table-component';
import { Link, NavLink } from 'react-router-dom';

import axios from 'axios';

import { useGlobalContext } from '../../AppContext';
import {
  FavoritesTokensTableDataProps,
  FavoritesWalletsTableDataProps,
} from '../../constants/interfaces';
import {
  add_favorite_token,
  remove_favorite_token,
  remove_favorite_wallet,
} from '../../utils/apiOperations';
import { fetchTokensDetailsFromServer } from '../../utils/hooks';
import { AddFavoriteToken } from '../../components/AddFavoriteToken';
import { AddFavoriteWallet } from '../../components/AddFavoriteWallet';
import { AddReminderToken } from '../../components/AddReminderToken';
import { BigButton } from '../../components/BigButton';
import { BigMenu } from '../../components/BigMenu';
import { Button } from '../../components/Button';
import { ConfirmAlert } from '../../components/ConfirmAlert';

import './Favorites.scss';

export const Favorites = () => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const handleThemeChange = () => {
      const newTheme = document.body.classList.contains('dark') ? 'light' : 'dark';
      setTheme(newTheme);
    };

    handleThemeChange();

    const observer = new MutationObserver(handleThemeChange);
    const config = { attributes: true, attributeFilter: ['class'] };
    observer.observe(document.body, config);

    return () => {
      observer.disconnect();
    };
  }, []);

  const { config, isMobile, favorites, setFavorites, setCustomSearch, setAddress, setPreparing } =
    useGlobalContext();

  // use state
  const [selectedType, setSelectedType] = useState<string>('wallets');
  const [showTableWallets, setShowTableWallets] = useState<JSX.Element>(<></>);
  const [showTableTokens, setShowTableTokens] = useState<JSX.Element>(<></>);
  const [tableDataWallets, setTableDataWallets] = useState<FavoritesWalletsTableDataProps[]>([]);

  const [tableDataTokens, setTableDataTokens] = useState<FavoritesTokensTableDataProps[]>([]);

  const [showAddNewWallet, setShowAddNewWallet] = useState<boolean>(false);
  const [showAddNewToken, setShowAddNewToken] = useState<boolean>(false);

  const [reminderCoingeckoId, setReminderCoingeckoId] = useState<string>();
  const [showAddNewReminder, setShowAddNewReminder] = useState<boolean>(false);

  // const variables
  const walletsColumns = [
    {
      name: 'Name',
      selector: (row: FavoritesWalletsTableDataProps) => row.name,
      sortable: false,
      width: '100px',
    },
    {
      name: 'Address',
      selector: (row: FavoritesWalletsTableDataProps) => row.address,
      sortable: false,
      width: '300px',
    },
    {
      name: 'Balance ($)',
      selector: (row: FavoritesWalletsTableDataProps) => row.balanceRaw,
      sortable: false,
      width: '120px',
      cell: (row: FavoritesWalletsTableDataProps) => row.balance,
    },
    {
      name: (
        <div className="pnl-head">
          <img src="/icons/graph.svg" className="head-image" /> 24H
        </div>
      ),
      width: '120px',
      selector: (row: FavoritesWalletsTableDataProps) => row.pnl24,
      cell: (row: FavoritesWalletsTableDataProps) => (
        <div
          className={`table-pnl-24 ${
            parseFloat(row.pnl24percentage) > 0
              ? 'green'
              : parseFloat(row.pnl24percentage) == 0
              ? ''
              : 'red'
          }`}
        >
          <span>{row.pnl24amount}</span>
          <strong>{row.pnl24percentage}</strong>
        </div>
      ),
    },
    {
      name: 'Token Change',
      width: '120px',
      cell: (row: FavoritesWalletsTableDataProps) => {
        const tmp = row?.tokenChanges?.[row.id];

        return (
          <div className={`token-change`}>
            <span className="added-token">+{tmp.added.length}</span>
            <span className="removed-token">-{tmp.removed.length}</span>
          </div>
        );
      },
    },
    {
      name: '',
      width: '180px',
      selector: (row: FavoritesWalletsTableDataProps) => row.button,
      cell: (row: FavoritesWalletsTableDataProps) => {
        return (
          <div className="table-actions">
            <NavLink
              onClick={() => {
                setCustomSearch(true);
                setAddress(row.address);
                setPreparing(true);
              }}
              to={{
                pathname: '/',
              }}
            >
              <img src={`/icons/eye-${theme}.svg`} alt="" />
            </NavLink>
            <img
              onClick={() =>
                confirmAlert({
                  closeOnClickOutside: false,
                  customUI: ({ onClose }) => {
                    return (
                      <ConfirmAlert title="Are you sure?" onClose={onClose}>
                        <div className="confirm-button-flex">
                          <BigButton type="light" size="small" onClick={onClose} name="No" />
                          <BigButton
                            type="dark"
                            size="small"
                            onClick={() => {
                              remove_favorite_wallet(row.id, setFavorites);
                              onClose();
                            }}
                            name="Yes, Delete it!"
                          />
                        </div>
                      </ConfirmAlert>
                    );
                  },
                })
              }
              src="/icons/trash.svg"
              alt=""
            />
          </div>
        );
      },
    },
  ];

  // const variables
  const tokensColumns = [
    {
      name: '',
      selector: (row: FavoritesTokensTableDataProps) => row.network,
      sortable: true,
      cell: (row: FavoritesTokensTableDataProps) => <CustomNetwork name={''} logo={row.logo} />,
      width: '40px',
    },
    {
      name: 'Token',
      selector: (row: FavoritesTokensTableDataProps) => row.token,
      sortable: true,
      cell: (row: FavoritesTokensTableDataProps) => <CustomToken data={row} />,
    },
    {
      name: 'Category',
      selector: (row: FavoritesTokensTableDataProps) => row.category,
      sortable: true,
      width: '120px',
    },
    {
      name: 'Price',
      selector: (row: FavoritesTokensTableDataProps) => row.price,
      sortable: true,
      width: '120px',
    },
    {
      name: (
        <div className="pnl-head">
          <img src="/icons/graph.svg" className="head-image" /> 24H
        </div>
      ),
      selector: (row: FavoritesTokensTableDataProps) => row.pnl24,
      width: '120px',
      cell: (row: FavoritesTokensTableDataProps) => (
        <div
          className={`table-pnl-24 ${
            parseFloat(row.pnl24percentage) > 0
              ? 'green'
              : parseFloat(row.pnl24percentage) == 0
              ? ''
              : 'red'
          }`}
        >
          <span>{row.pnl24amount}</span>
          <strong>{row.pnl24percentage}</strong>
        </div>
      ),
    },
    {
      name: <img src="/icons/diamond.svg" className="head-image" />,
      selector: (row: FavoritesTokensTableDataProps) => row.holders,
      width: '120px',
    },
    {
      name: 'Marketcap',
      selector: (row: FavoritesTokensTableDataProps) => row.marketcap,
      sortable: true,
    },
    {
      name: 'Trend Graph',
      selector: (row: FavoritesTokensTableDataProps) => row.sparkline,
      sortable: false,

      cell: (row: FavoritesTokensTableDataProps) => (
        <CustomSparkline
          sparkline={row.sparkline}
          color={
            parseFloat(row.pnl24percentage) > 0
              ? 'd5fe88'
              : parseFloat(row.pnl24percentage) == 0
              ? 'ffffff'
              : 'e32a73'
          }
        />
      ),
    },
    {
      name: '',
      width: '80px',
      cell: (row: FavoritesTokensTableDataProps) => {
        console.log(favorites, row.coingeckoid);
        return (
          <div className="table-actions">
            <img
              onClick={() =>
                confirmAlert({
                  closeOnClickOutside: false,
                  customUI: ({ onClose }) => {
                    return (
                      <ConfirmAlert title="Are you sure?" onClose={onClose}>
                        <Button color="one" size="large" onClick={onClose} label="No" />
                        <Button
                          color="three"
                          size="large"
                          onClick={() => {
                            remove_favorite_token(favorites.tokens[row.coingeckoid], setFavorites);
                            onClose();
                          }}
                          label="Yes, Delete it!"
                        />
                      </ConfirmAlert>
                    );
                  },
                })
              }
              src="/icons/trash.svg"
              alt=""
            />

            <img
              onClick={() => {
                setReminderCoingeckoId(row.coingeckoid);
                setShowAddNewReminder(!showAddNewReminder);
              }}
              src="/icons/reminder.svg"
              alt=""
            />
          </div>
        );
      },
    },
  ];

  interface CustomTokenProps {
    data: FavoritesTokensTableDataProps;
  }

  const CustomToken = (props: CustomTokenProps) => {
    return (
      <>
        <div className="icon-container">
          <img
            src={props.data.tokenLogo}
            className={`tokenLogo ${props.data.tokenLogo.includes('/networks/') ? 'native' : ''}`}
          />
        </div>

        <div className="token-name-container">{props.data.token}</div>
        <div className="chart-icon" onClick={() => openGraphLink(props.data.address)}>
          <img src={`/icons/chart-${theme}.svg`} alt="" />
        </div>
      </>
    );
  };

  interface CustomNetworkProps {
    name: string;
    logo: string;
    ads?: boolean;
  }

  const CustomNetwork = (props: CustomNetworkProps) => {
    return (
      <>
        <div className="icon-container">
          <img
            src={props.logo}
            className={props.logo && props.logo.includes('/networks/') ? 'native' : ''}
          />
        </div>
        {props.name}
        {props.ads && (
          <div
            className="adsBuyTableLink"
            onClick={(e: any) => e.target.classList.toggle('active')}
          >
            Buy
            <div className="details">
              <div
                className="close"
                onClick={(e: any) => e.target.parentNode.parentNode.classList.toggle('active')}
              >
                &times;
              </div>
              <div className="sponsored">
                <div className="sponsored-warning">
                  <img src="/images/icon-dark.svg" />
                  Sponsored
                </div>
                <div className="binance">
                  <div className="header-part">
                    <img src="/sponsored/binance/binance.png" /> Binance
                  </div>
                  <div className="description-part">
                    Buy Crypto with Fees as Low as 0%
                    <Link target="_blank" to={{ pathname: 'https://www.binance.com' }}>
                      Start Trading
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  interface CustomSparklineProps {
    sparkline: string;
    color: string;
  }

  // 5cb85c
  const CustomSparkline = (props: CustomSparklineProps) => {
    const arrOfStr = props.sparkline ? props.sparkline.split(',') : [0];
    const arrOfNum: number[] = [];
    arrOfStr.forEach(str => {
      arrOfNum.push(Number(str));
    });
    const min = Math.min(...arrOfNum);
    const max = Math.max(...arrOfNum);
    return (
      <>
        <div className="sparkline-container" style={{ width: '100px' }}>
          <img
            src={`https://chart.googleapis.com/chart?chco=${props.color}&chf=bg,s,000000&cht=lc:nda&chds=${min},${max}&chs=100x34&chd=t:${props.sparkline}`}
          />
        </div>
      </>
    );
  };
  // functions
  const openGraphLink = async (address: string): Promise<void> => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_API}/fetch-graph-link.php?address=${address}`,
        withCredentials: true,
      });
      if (response && response.data) {
        const result = JSON.parse(response.data.data);
        console.log(result);
        if (result && result.results) {
          window.open(
            `https://www.dextools.io/app/en/ether/pair-explorer/${result.results[0].id.pair}`,
          );
        }
      }
    } catch (error) {
      console.error(error);
    }
  };
  // wallets
  const renderShowTable = (data: any): void => {
    if (selectedType === 'wallets') {
      setShowTableWallets(
        <DataTable
          noDataComponent={
            <div className="data-table-no-record">
              <h1>Favorites</h1>
              <hr />
              <div className="light-button-container">
                <div className="light-button">
                  <Button
                    onClick={() => setShowAddNewWallet(!showAddNewWallet)}
                    color="one"
                    size="large"
                    label={`Add New ${selectedType === 'wallets' ? 'Wallet' : 'Token'}`}
                  />
                </div>
              </div>
            </div>
          }
          pagination
          columns={walletsColumns}
          data={data}
          expandableRows={true}
          expandableRowsComponent={ExpandedComponentWallet}
        />,
      );
    } else {
      setShowTableTokens(
        <DataTable
          noDataComponent={
            <div className="data-table-no-record">
              <h1>Favorites</h1>
              <hr />
              <div className="light-button-container">
                <div className="light-button">
                  <Button
                    onClick={() => setShowAddNewToken(!showAddNewToken)}
                    color="one"
                    size="large"
                    label={`Add New ${selectedType === 'wallets' ? 'Wallet' : 'Token'}`}
                  />
                </div>
              </div>
            </div>
          }
          pagination
          columns={tokensColumns}
          data={data}
        />,
      );
    }
  };

  const calculateTokenChanges = (data: any): any[] => {
    const tmp = data.data.tokenChanges[data.data.id];
    const tokens: any = [];
    console.log(tmp);
    if (tmp && tmp.added && tmp.coins) {
      Object.values(tmp.added).filter((item: any) => {
        tokens.push({
          coingeckoid: item['coingeckoid'],
          network: 'erc20',
          name: tmp.coins[item['coingeckoid']]['name'],
          symbol: tmp.coins[item['coingeckoid']]['symbol'].toUpperCase(),
          amount: item['amount'],
          amountDeposited: item['amount'],
          amountWithdrawn: 0,
          balance: item['balance'],
          price: item['price'],
          pnl24percentage: item['pnl24percentage'],
          pnlAmount: (item['balance'] / 100) * item['pnl24percentage'],
        });
      });
    }

    if (tmp && tmp.removed && tmp.coins) {
      Object.values(tmp.removed).filter((item: any) => {
        tokens.push({
          coingeckoid: item['coingeckoid'],
          network: 'erc20',
          name: tmp.coins[item['coingeckoid']]['name'],
          symbol: tmp.coins[item['coingeckoid']]['symbol'].toUpperCase(),
          amount: item['amount'],
          amountWithdrawn: item['amount'],
          amountDeposited: 0,
          balance: item['balance'],
          price: item['price'],
          pnl24percentage: item['pnl24percentage'],
          pnlAmount: (item['balance'] / 100) * item['pnl24percentage'],
        });
      });
    }

    return tokens;
  };

  const ExpandedComponentWallet = (data: any) => {
    const tokenChanges = calculateTokenChanges(data);
    console.log(favorites);
    console.log(tokenChanges);
    console.log(data);
    return (
      <>
        {tokenChanges.length > 0 && (
          <div className="rdt_TableHeadRow">
            <div className="rdt_TableCol" style={{ width: '52px' }}></div>
            <div className="rdt_TableCol" style={{ width: '100px' }}>
              Symbol
            </div>
            <div className="rdt_TableCol" style={{ width: '100px' }}>
              Token
            </div>
            <div className="rdt_TableCol" style={{ width: '120px' }}>
              Balance
            </div>
            <div className="rdt_TableCol" style={{ width: '120px' }}>
              PNL 24H
            </div>
            <div className="rdt_TableCol" style={{ width: '120px' }}>
              Amount
            </div>
            <div className="rdt_TableCol" style={{ width: '120px' }}></div>
          </div>
        )}
        {tokenChanges.length > 0 &&
          tokenChanges.map((item: any, index: number) => {
            return (
              <div key={index} role="row" className="rdt_TableRow expandedRow">
                <div className="rdt_TableCell" style={{ width: '52px' }}>
                  <img
                    src={`/icons/networks/${item.network}.png`}
                    className="expanded-icon"
                    alt=""
                  />
                </div>
                <div className="rdt_TableCell" style={{ width: '100px' }}>
                  {item.symbol}
                </div>
                <div className="rdt_TableCell" style={{ width: '100px' }}>
                  {item.name}
                </div>
                <div className="rdt_TableCell" style={{ width: '120px' }}>
                  {`$${parseFloat(parseFloat(item.balance).toFixed(5)).toString()}`}
                </div>
                <div className="rdt_TableCell" style={{ width: '120px' }}>
                  <div
                    className={`table-pnl-24 ${
                      parseFloat(item.pnl24percentage) > 0
                        ? 'green'
                        : parseFloat(item.pnl24percentage) == 0
                        ? ''
                        : 'red'
                    }`}
                  >
                    <span>{`$${parseFloat(
                      parseFloat(item.pnlAmount).toFixed(5).toString(),
                    )}`}</span>
                    <strong>{`${parseFloat(
                      parseFloat(item.pnl24percentage).toFixed(2).toString(),
                    )}%`}</strong>
                  </div>
                </div>
                <div className="rdt_TableCell" style={{ width: '120px' }}>
                  {item.amountDeposited ? '+' : '-'}
                  {item.amountDeposited
                    ? item.amountDeposited
                        .toFixed(2)
                        .toString()
                        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
                    : item.amountWithdrawn
                        .toFixed(2)
                        .toString()
                        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}
                </div>
                <div className="rdt_TableCell" style={{ width: '120px' }}>
                  <div className="table-actions">
                    <img
                      onClick={() => {
                        if (favorites.tokens[item.coingeckoid]) {
                          remove_favorite_token(favorites.tokens[item.coingeckoid], setFavorites);
                        } else {
                          console.log(favorites.tokens);
                          console.log(item.coingeckoid);
                          add_favorite_token(item.coingeckoid, setFavorites);
                        }
                      }}
                      className={`${
                        favorites.tokens && favorites.tokens[item.coingeckoid] ? 'active' : ''
                      }`}
                      src="/icons/star2.svg"
                      alt=""
                    />
                    <img
                      onClick={() => {
                        setReminderCoingeckoId(item.coingeckoid);
                        setShowAddNewReminder(!showAddNewReminder);
                      }}
                      src="/icons/reminder.svg"
                      alt=""
                    />
                  </div>
                </div>
              </div>
            );
          })}
        {tokenChanges.length == 0 && (
          <div role="row" className="rdt_TableRow expandedRow">
            <div className="expandedColumn disabled"></div>
            <div className="expandedColumn">
              There is no data at the moment, please try again later.
            </div>
            <div className="expandedColumn"></div>
          </div>
        )}
      </>
    );
  };

  const onClose = (): void => {
    setShowAddNewWallet(false);
    setShowAddNewToken(false);
    setShowAddNewReminder(false);
  };

  useEffect(() => {
    const initialWalletsTableData: FavoritesWalletsTableDataProps[] = new Array();
    const initialTokensTableData: FavoritesTokensTableDataProps[] = new Array();

    if (config && favorites) {
      if (selectedType === 'wallets') setTableDataWallets(initialWalletsTableData);
      else setTableDataTokens(initialTokensTableData);
      setFavorites(false);
    } else {
      if (selectedType === 'wallets') setTableDataWallets(initialWalletsTableData);
      else setTableDataTokens(initialTokensTableData);
    }
  }, [selectedType]);

  useEffect(() => {
    renderShowTable(tableDataWallets);
    if (isMobile && document.getElementsByClassName('rdt_Table')[0]) {
      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 50,
          behavior: 'smooth',
        });
      }, 200);
      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 60,
          behavior: 'smooth',
        });
      }, 210);
      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 70,
          behavior: 'smooth',
        });
      }, 220);

      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 0,
          behavior: 'smooth',
        });
      }, 800);
    }
  }, [tableDataWallets]);

  useEffect(() => {
    renderShowTable(tableDataTokens);
    if (isMobile && document.getElementsByClassName('rdt_Table')[0]) {
      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 50,
          behavior: 'smooth',
        });
      }, 200);
      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 60,
          behavior: 'smooth',
        });
      }, 210);
      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 70,
          behavior: 'smooth',
        });
      }, 220);

      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 0,
          behavior: 'smooth',
        });
      }, 800);
    }
  }, [tableDataTokens]);

  useEffect(() => {
    const tmpDataWallets: FavoritesWalletsTableDataProps[] = new Array();
    const tmpDataTokens: FavoritesTokensTableDataProps[] = new Array();
    if (selectedType === 'wallets') {
      if (favorites && favorites.wallets) {
        Object.values(favorites!.wallets).forEach((item: any) => {
          tmpDataWallets.push({
            id: Number(item.id),
            name: item.name,
            address: item.address,
            balanceRaw: item.balance,
            balance: `$${parseFloat(item.balance)
              .toString()
              .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`,
            pnl24: `$${item.pnlAmount}(${item.pnl24Percentage}%)`,
            pnl24percentage: `${parseFloat(
              parseFloat(item.pnl24Percentage).toFixed(2).toString(),
            )}%`,
            pnl24amount: `$${
              item.pnlAmount ? parseFloat(parseFloat(item.pnlAmount).toFixed(5).toString()) : 0
            }`,
            button: true,
            tokenChanges: favorites.wallets,
          });
        });

        setTableDataWallets(tmpDataWallets);
      }
    } else if (selectedType === 'tokens') {
      if (favorites && favorites.tokens) {
        fetchTokensDetailsFromServer(Object.keys(favorites.tokens)).then((response: any) => {
          console.log(response);
          console.log(config.globalNetworks);
          Object.values(response).forEach((item: any) => {
            console.log(item);
            if (item.coingeckoid)
              tmpDataTokens.push({
                coingeckoid: item.coingeckoid,
                network: item.name,
                logo: `/icons/networks/${item.platform}.png`,
                token: item.name,
                symbol: item.symbol,
                category: item.category,
                address: item.address,
                tokenLogo: item.image,
                holders: item.holders,
                pnl24: `$${item.pnl24hamount}(${item.pnl24hpercentage}%)`,
                pnl24amount: `$${parseFloat(parseFloat(item.pnl24hamount).toFixed(5).toString())}`,
                pnl24percentage: `${parseFloat(
                  parseFloat(item.pnl24hpercentage).toFixed(2).toString(),
                )}%`,
                sparkline: item.graphdata || '',
                high24h: item.high24h,
                low24h: item.low24h,
                price: item.price,
                ath: item.ath,
                atl: item.atl,
                marketcap: item.marketcap
                  ? item.marketcap.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
                  : '0',
                networkKey: 'erc20',
                color:
                  item.graphLastDirection === 'up'
                    ? '90ff00'
                    : item.graphLastDirection === 'down'
                    ? 'ff0000'
                    : 'cccccc',
              });
          });

          setTableDataTokens(tmpDataTokens);
        });
      }
    }
  }, [favorites.wallets]);

  return (
    <div className="favorites">
      <BigMenu horizontal={true} />
      <div className="button-container mt-5 mb-5">
        <div onClick={() => setSelectedType('wallets')}>
          <BigButton name="Wallets" type={`${selectedType === 'wallets' ? 'light' : 'dark'}`} />
        </div>
        <div onClick={() => setSelectedType('tokens')}>
          <BigButton name="Tokens" type={`${selectedType === 'tokens' ? 'light' : 'dark'}`} />
        </div>
      </div>
      {config && selectedType === 'wallets' && (
        <div className={`custom-table-${theme} mt-5 padding-left`}>
          <div className="table-border-top" />
          <div>{showTableWallets}</div>
          <div className="table-border-bottom" />
        </div>
      )}

      {config && selectedType === 'tokens' && (
        <div className={`custom-table-${theme} mt-5 padding-left`}>
          <div className="table-border-top" />
          <div>{showTableTokens}</div>
          <div className="table-border-bottom" />
        </div>
      )}

      {selectedType === 'wallets' &&
        favorites &&
        Object.values(favorites.wallets) &&
        Object.values(favorites.wallets).length > 0 && (
          <div className="mt-5">
            <div className="light-button-container">
              <div className="light-button">
                <Button
                  onClick={() => setShowAddNewWallet(!showAddNewWallet)}
                  color="one"
                  size="large"
                  label="Add New Wallet"
                />
              </div>
            </div>
          </div>
        )}
      {showAddNewWallet && <AddFavoriteWallet onClose={onClose} />}

      {selectedType === 'tokens' &&
        favorites &&
        Object.values(favorites.tokens) &&
        Object.values(favorites.tokens).length > 0 && (
          <div className="mt-5">
            <div className="light-button-container">
              <div className="light-button">
                <Button
                  onClick={() => setShowAddNewToken(!showAddNewToken)}
                  color="one"
                  size="large"
                  label="Add New Token"
                />
              </div>
            </div>
          </div>
        )}
      {showAddNewToken && <AddFavoriteToken onClose={onClose} />}
      {showAddNewReminder && reminderCoingeckoId && (
        <AddReminderToken coingeckoid={reminderCoingeckoId} onClose={onClose} />
      )}
    </div>
  );
};
